import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE;

export default function FamilyPage({
  user,
  embedded = false,
  targetUserId = null,
  allowPublicBrowse = false,
}) {
  const { userId: routeId } = useParams();
  const [tree, setTree] = useState(null);
  const [error, setError] = useState(null);
  const [browseId, setBrowseId] = useState(null);
  const token = localStorage.getItem("kami_token");
  const targetId = routeId || targetUserId || user?.id || browseId;

  useEffect(() => {
    if (targetId || !allowPublicBrowse) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE}/leaderboard?type=level&limit=1`,
        );
        const id = data?.entries?.[0]?.userId;
        if (!cancelled && id) setBrowseId(id);
        else if (!cancelled) {
          setError("No public family trees to show yet.");
        }
      } catch {
        if (!cancelled) {
          setError("Could not load a public family tree.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetId, allowPublicBrowse]);

  useEffect(() => {
    if (!targetId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/family/${targetId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) {
          setTree(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || "Could not load family tree.");
          setTree(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetId, token]);

  if (!targetId && !allowPublicBrowse) {
    return (
      <div className={embedded ? "portal-embed" : "portal-page"}>
        <div className="profile-login-gate">
          {!embedded && <h1>Family</h1>}
          <p>Log in to view your family tree, or open /family/&lt;userId&gt;.</p>
          <button
            type="button"
            className="nav-login-btn"
            onClick={() => {
              window.location.href = `${API_BASE}/auth/login`;
            }}
          >
            Log in with Discord
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={embedded ? "portal-embed" : "portal-page"}>
        <div className="profile-login-gate">
          {!embedded && <h1>Family</h1>}
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!tree) {
    return (
      <div className={embedded ? "portal-embed" : "portal-page"}>
        <div className="page-loading">
          <div className="sync-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "portal-embed" : "portal-page"}>
      <header className="portal-page-head">
        {!embedded && <h1>Family tree</h1>}
        <p>
          {tree.guildName || "Nocturne"} · bonds & adoptions
          {browseId && !user?.id && !routeId && !targetUserId
            ? " · community preview"
            : ""}
        </p>
      </header>
      <div className="family-rows">
        {(tree.rows || []).map((row, ri) => (
          <div key={`row-${ri}`} className="family-row">
            {(row.groups || []).map((group, gi) => (
              <div key={`g-${ri}-${gi}`} className="family-group">
                {(group.units || []).map((unit, ui) => (
                  <div
                    key={`u-${ri}-${gi}-${ui}`}
                    className={`family-unit${unit.type === "pair" ? " pair" : ""}`}
                  >
                    {(unit.members || []).map((m) => (
                      <Link
                        key={m.userId}
                        to={`/u/${m.userId}`}
                        className="family-node"
                      >
                        {m.avatarUrl ? (
                          <img src={m.avatarUrl} alt="" />
                        ) : (
                          <span className="avatar-fallback" />
                        )}
                        <strong>{m.username}</strong>
                        <span>{m.subtitle}</span>
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
      {!tree.rows?.length && (
        <p className="portal-muted">
          No bonds or adoptions yet. Use /fun bond and /fun adoption in Discord.
        </p>
      )}
    </div>
  );
}
